import { inject, injectable } from "inversify";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import {
  CPLConnectSessionTokensModel,
  ExternalAccessTokenModel,
  ICPLAuthAuthenticationClient,
  LoginRequestModel,
} from "./api/cplauthapi/CPLAuthAPI";
import { ISessionService } from "./SessionService";
import { ICPLAuthenticationClient, RPMUserVerificationModel } from "./api/cplapi/CPLAPI";

export interface IAuthenticationService {
  logIn(emailAddress: string, password: string): Promise<string>;
  logOut(): Promise<void>;
  verifyRPMUser(username: string, password: string): Promise<string>;
  getNewSessionTokens(): Promise<void>;
  verifyUserFromLoginToken(token: string): Promise<string>;
  verifyExternalAccessUserFromToken(token: string): Promise<ExternalAccessTokenModel>;
}

@injectable()
export class AuthenticationService implements IAuthenticationService {
  @inject(SERVICE_TYPES.CPLAuthAuthenticationClient)
  cplAuthAuthenticationClient!: ICPLAuthAuthenticationClient;
  @inject(SERVICE_TYPES.SessionService) sessionService!: ISessionService;
  @inject(SERVICE_TYPES.CPLAuthenticationClient) cplAuthenticationClient!: ICPLAuthenticationClient;

  async logIn(emailAddress: string, password: string): Promise<string> {
    const response = await this.cplAuthAuthenticationClient.verifyCPLUser(
      new LoginRequestModel({ emailAddress, password })
    );
    await this.sessionService.setSessionTokens(response);
    const cplToken = await this.sessionService.getCPLSessionToken();
    if (!cplToken) {
      throw new Error("Invalid Login");
    }

    return cplToken;
  }

  async logOut(): Promise<void> {
    await this.sessionService.clearCPLAuthToken();
  }

  async verifyRPMUser(username: string, password: string): Promise<string> {
    return await this.cplAuthenticationClient.verifyRPMUser(new RPMUserVerificationModel({ username, password }));
  }

  async getNewSessionTokens(): Promise<void> {
    const cplToken = await this.cplAuthAuthenticationClient.getNewCPLToken();
    const imageToken = await this.cplAuthAuthenticationClient.getNewCPLImageToken();
    await this.sessionService.setSessionTokens(
      new CPLConnectSessionTokensModel({
        cplapiToken: cplToken,
        imageToken: imageToken,
      })
    );
  }

  async verifyUserFromLoginToken(token: string): Promise<string> {
    const response = await this.cplAuthAuthenticationClient.verifyCPLUserFromLoginToken(token);
    await this.sessionService.setSessionTokens(response);
    const cplToken = await this.sessionService.getCPLSessionToken();
    if (!cplToken) {
      throw new Error("Invalid Login");
    }
    return cplToken;
  }

  async verifyExternalAccessUserFromToken(token: string): Promise<ExternalAccessTokenModel> {
    const response = await this.cplAuthAuthenticationClient.verifyCPLExternalAccessUser(token);
    await this.sessionService.setExternalAccessSessionTokens(response, token);
    return response;
  }
}
